// Inbox — sortable, filterable list of active deals.

import { useMemo, useState } from "react";
import { findMember, useAppData, useMe } from "../store";
import type { Deal, Status } from "../types";
import {
  Btn, Chip, Pill, Score, ScoreGlyph, TextInput, Avatar,
} from "../components/Primitives";
import { Ic, Mark } from "../components/Icon";
import { Logo } from "../components/Logo";
import { OwnerPopover } from "../components/OwnerPopover";
import { ProcessingChip } from "../components/ProcessingChip";

const ROW_COLS = "1.6fr 0.8fr 1fr 0.9fr 0.7fr 28px 96px";

type OwnerFilter = "all" | "mine" | "unassigned";
type SortKey = "score" | "time" | "company";

function InboxEmail() {
  const { data } = useAppData();
  const [copied, setCopied] = useState(false);
  const addr = data?.firm.inboundEmail;
  if (!addr) return null;
  const copy = async () => {
    await navigator.clipboard.writeText(addr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button className="inbox-email-chip" onClick={copy} title="Click to copy">
      <Ic name="mail" size={12} />
      <span>{addr}</span>
      {copied && <span className="inbox-email-tick"><Ic name="check" size={10} /></span>}
    </button>
  );
}

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "score", label: "Score" },
  { key: "time", label: "Time" },
  { key: "company", label: "Company" },
];

function SortPicker({ value, onChange }: { value: SortKey; onChange: (k: SortKey) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="sort-picker">
      <button className="sort-picker-btn" onClick={() => setOpen(!open)}>
        <Ic name="sliders" size={13} />
        Sort: {SORT_OPTIONS.find((o) => o.key === value)!.label}
      </button>
      {open && (
        <>
          <div className="sort-picker-backdrop" onClick={() => setOpen(false)} />
          <div className="sort-picker-menu">
            {SORT_OPTIONS.map((o) => (
              <button
                key={o.key}
                className={"sort-picker-item" + (o.key === value ? " active" : "")}
                onClick={() => { onChange(o.key); setOpen(false); }}
              >
                {o.label}
                {o.key === value && <Ic name="check" size={12} />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface Props {
  onOpen: (id: string) => void;
  /** When set, only show this member's deals (Team → "view in inbox"). */
  ownerFilterId?: string | null;
  onClearOwnerFilter?: () => void;
}

export function InboxScreen({ onOpen, ownerFilterId, onClearOwnerFilter }: Props) {
  const { data, deleteDeal, patchDeal } = useAppData();
  const [ownerPop, setOwnerPop] =
    useState<{ anchor: DOMRect; dealId: string; current: string | null } | null>(null);
  const me = useMe();
  const [owner, setOwner] = useState<OwnerFilter>("all");
  const [status, setStatus] = useState<"all" | Status>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("score");

  const deals = data?.deals ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = deals.filter((d) => {
      if (ownerFilterId && d.owner !== ownerFilterId) return false;
      if (owner === "mine" && d.owner !== me?.id) return false;
      if (owner === "unassigned" && d.owner) return false;
      if (status !== "all" && d.status !== status) return false;
      if (q && !d.co.toLowerCase().includes(q) && !d.founders.toLowerCase().includes(q)) return false;
      return true;
    });
    const sorted = [...list];
    if (sort === "score") sorted.sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
    if (sort === "company") sorted.sort((a, b) => a.co.localeCompare(b.co));
    return sorted;
  }, [deals, owner, status, search, sort, me, ownerFilterId]);

  const filterMember = ownerFilterId
    ? findMember(data?.config.team, ownerFilterId)
    : undefined;

  const stats = useMemo(() => {
    const news = deals.filter((d) => /min|^[0-9]+\s*h/.test(d.time || "")).length;
    const scored = deals.filter((d) => typeof d.score === "number");
    const avg = scored.length
      ? Math.round(scored.reduce((s, d) => s + (d.score ?? 0), 0) / scored.length)
      : 0;
    const sectors: Record<string, number> = {};
    deals.forEach((d) => { sectors[d.sector] = (sectors[d.sector] || 0) + 1; });
    const top = Object.entries(sectors).sort((a, b) => b[1] - a[1])[0];
    return { news, avg, top: top ? top[0] : "—" };
  }, [deals]);

  return (
    <div className="page">
      <div className="topbar">
        <h1>Inbox</h1>
        <span className="crumb">· {deals.length} active deals</span>
        <InboxEmail />
        <ProcessingChip />
        <span className="grow" />
        <TextInput
          search ico="search" placeholder="Search company, founder…"
          value={search} onChange={setSearch} style={{ width: 240 }}
        />
      </div>

      <div className="meta-strip">
        <div className="meta-strip-item">
          <div className="n">{stats.news}</div>
          <div className="l">new today</div>
        </div>
        <div className="meta-strip-item">
          <div className="n">{stats.avg}</div>
          <div className="l">average score</div>
        </div>
        <div className="meta-strip-item">
          <div className="n" style={{ fontFamily: "var(--serif)", fontWeight: 500 }}>{stats.top}</div>
          <div className="l">top sector this week</div>
        </div>
      </div>

      <div className="filter-bar">
        {filterMember && (
          <>
            <Chip on onClick={() => onClearOwnerFilter?.()}>
              <Avatar id={filterMember.id} size="xs" />
              <span style={{ marginLeft: 4 }}>{filterMember.name}</span>
              <span style={{ marginLeft: 5, display: "inline-flex" }}><Ic name="x" size={10} /></span>
            </Chip>
            <div style={{ width: 1, height: 18, background: "var(--border)", margin: "0 4px" }} />
          </>
        )}
        <Chip on={owner === "all"} onClick={() => setOwner("all")}>
          All <span className="count" style={{ marginLeft: 4 }}>{deals.length}</span>
        </Chip>
        <Chip on={owner === "mine"} onClick={() => setOwner("mine")}>My deals</Chip>
        <Chip on={owner === "unassigned"} onClick={() => setOwner("unassigned")}>Unassigned</Chip>
        <div style={{ width: 1, height: 18, background: "var(--border)", margin: "0 4px" }} />
        {(["all", "new", "review", "diligence"] as const).map((k) => (
          <Chip key={k} on={status === k} onClick={() => setStatus(k)}>
            {k === "all" ? "All status" : k === "review" ? "Reviewed" : k === "diligence" ? "In diligence" : "New"}
          </Chip>
        ))}
        <span className="grow" />
        <SortPicker value={sort} onChange={setSort} />
      </div>

      <div className="scroll" style={{ background: "var(--surface)" }}>
        <div className="row-head" style={{ ["--row-cols" as any]: ROW_COLS }}>
          <span>Company</span>
          <span>Stage</span>
          <span>Sector</span>
          <span>Geography</span>
          <span>Status</span>
          <span>Owner</span>
          <span className="r">Score</span>
        </div>
        {filtered.length === 0 ? (
          <div className="empty">
            <div className="mark"><Mark size={32} /></div>
            <h3>Nothing here yet</h3>
            <p>{owner === "unassigned"
              ? "Every active deal has an owner — nice."
              : "Adjust your filters or wait for the next inbound to land."}</p>
          </div>
        ) : filtered.map((d: Deal) => (
          <div
            key={d.id} className={"row" + (d.source === "upload" ? " row-deletable" : "")}
            style={{ ["--row-cols" as any]: ROW_COLS }}
            onClick={() => onOpen(d.id)}
          >
            <div className="row-co" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
              <Logo domain={d.domain} co={d.co} size={28} />
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                <span className="co" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {d.co}
                  {(d.precedents ?? []).some((p) => p.pin === "founder") && (
                    <span style={{
                      fontSize: 9.5, fontWeight: 600, color: "var(--accent)",
                      background: "var(--accent-soft)", padding: "1px 6px",
                      borderRadius: 8, whiteSpace: "nowrap",
                    }}>
                      Repeat founder
                    </span>
                  )}
                  {(d.precedents?.length ?? 0) > 0 && !(d.precedents ?? []).some((p) => p.pin === "founder") && (
                    <span style={{
                      fontSize: 9.5, fontWeight: 600, color: "var(--ink-3)",
                      background: "var(--raised)", padding: "1px 6px",
                      borderRadius: 8, whiteSpace: "nowrap",
                    }}>
                      {d.precedents!.length} similar
                    </span>
                  )}
                </span>
                <span className="meta">{d.founders} · {d.time}</span>
              </div>
            </div>
            <span className="row-cell">{d.stage}</span>
            <span className="row-cell">{d.sector}</span>
            <span className="row-cell">{d.geo}</span>
            <Pill kind={d.status} />
            <span
              title={d.owner ? "Reassign owner" : "Assign owner"}
              onClick={(e) => {
                e.stopPropagation();
                setOwnerPop({
                  anchor: (e.currentTarget as HTMLElement).getBoundingClientRect(),
                  dealId: d.id,
                  current: d.owner ?? null,
                });
              }}
              style={{ cursor: "pointer", display: "inline-flex" }}
            >
              <Avatar id={d.owner} />
            </span>
            <div className="row-score">
              <ScoreGlyph value={d.score} />
              <span className="score num">{d.score ?? "—"}</span>
            </div>
            {d.source === "upload" && (
              <button
                className="row-delete-btn"
                title="Delete this deal"
                onClick={(e) => { e.stopPropagation(); deleteDeal(d.id); }}
              >
                <Ic name="trash" size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
      {ownerPop && (
        <OwnerPopover
          anchor={ownerPop.anchor}
          currentOwner={ownerPop.current}
          onPick={(ownerId) => {
            void patchDeal(ownerPop.dealId, { owner: ownerId });
            setOwnerPop(null);
          }}
          onClose={() => setOwnerPop(null)}
        />
      )}
    </div>
  );
}
